import { createServerFn } from '@tanstack/react-start';
import { ArtifactsRules } from '@tessera-labs-inc/abaplint-core';
import z from 'zod';

import { CONFIGURED_RULE_KEYS } from '@/abap/configs';
import type { AbapLintRuleKeysExtended, AbapLintRuleMeta } from '@/abap/types';
import { getAbapStaticFixDiagnostics } from '@/server/abap/abap-static-fixes-core';
import type { TLAIAbapStaticFixListResponse, TLAINextFixAbapRequest, TLAINextFixAbapResponse } from '@/types/api';

const getRulesMeta = () => {
  return ArtifactsRules.getRules().reduce((allRules, rule) => {
    const meta = rule.getMetadata();
    allRules.set(meta.key as AbapLintRuleKeysExtended, meta);
    return allRules;
  }, new Map<AbapLintRuleKeysExtended, AbapLintRuleMeta>());
};

export const abapStaticFixList = createServerFn({ method: 'GET' }).handler(
  async (): Promise<TLAIAbapStaticFixListResponse> => {
    const rulesMeta = getRulesMeta();
    return {
      staticFixes: CONFIGURED_RULE_KEYS.map((key) => {
        const metadata = rulesMeta.get(key);
        return {
          key,
          description: metadata?.shortDescription ?? metadata?.title ?? key,
          title: metadata?.title,
          tags: metadata?.tags,
          metadata,
        };
      }),
    };
  },
);

export const abapStaticFixes = createServerFn({ method: 'POST' })
  .inputValidator(
    z.object({
      code: z.string(),
      session_id: z.string().optional(),
      type: z.string().optional(),
      filename: z.string(),
      variant: z.enum(['all', 'general-only']).optional(),
    }) satisfies z.ZodType<TLAINextFixAbapRequest>,
  )
  .handler(async ({ data: { code, type, filename, variant } }): Promise<TLAINextFixAbapResponse> => {
    try {
      const diagnostics = await getAbapStaticFixDiagnostics({ code, type, filename, variant });
      return { diagnostics };
    } catch (error) {
      console.error('[FIX ABAP] Error fixing ABAP code:', error);
      throw Error('Error fixing ABAP code');
    }
  });
